import { BackendUser, User } from '../types';
import { storageGet, storageSet } from './storage';

/**
 * Мультиаккаунт ВКонтакте поверх существующего «одиночного» хранилища.
 * Активный аккаунт остаётся в `ssmm-token-vk` + `ssmm-vk-user-cache` (как раньше —
 * это читают все текущие флоу и postMessage). Дополнительно ведём список всех
 * VK-аккаунтов, в которые входили, чтобы быстро переключаться и добавлять новые.
 */
const VK_ACCOUNTS_KEY = 'ssmm-vk-accounts';
const ACTIVE_TOKEN_KEY = 'ssmm-token-vk';
const ACTIVE_CACHE_KEY = 'ssmm-vk-user-cache';

export const MAX_VK_ACCOUNTS = 5;

export interface StoredVkAccount {
  uid: string;
  vkId?: number;
  token: string;
  user: User;
  backendUser: BackendUser;
  addedAt: number;
}

/** Стабильный ключ аккаунта: uid бэкенда (или vkId / id как запасной). */
export function vkAccountUid(backendUser?: BackendUser, user?: User): string {
  return String(backendUser?.uid || backendUser?.vkId || user?.id || '');
}

export function listVkAccounts(): StoredVkAccount[] {
  try {
    const raw = storageGet(VK_ACCOUNTS_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(arr)) return [];
    return arr.filter((a): a is StoredVkAccount => !!a && typeof a === 'object' && !!(a as StoredVkAccount).uid && !!(a as StoredVkAccount).token);
  } catch {
    return [];
  }
}

function writeVkAccounts(list: StoredVkAccount[]): void {
  try {
    storageSet(VK_ACCOUNTS_KEY, JSON.stringify(list.slice(0, MAX_VK_ACCOUNTS)));
  } catch {
    /* ignore quota/serialization errors */
  }
}

/** Добавляет или обновляет VK-аккаунт в списке (по uid). */
export function upsertVkAccount(token: string, user: User, backendUser: BackendUser): StoredVkAccount[] {
  const uid = vkAccountUid(backendUser, user);
  if (!uid || !token) return listVkAccounts();

  const list = listVkAccounts();
  const existing = list.find((a) => a.uid === uid);
  const entry: StoredVkAccount = {
    uid,
    vkId: backendUser?.vkId ?? existing?.vkId,
    token,
    user,
    backendUser,
    addedAt: existing?.addedAt ?? Date.now(),
  };
  const next = existing ? list.map((a) => (a.uid === uid ? entry : a)) : [...list, entry];
  writeVkAccounts(next);
  return next;
}

export function removeVkAccount(uid: string): StoredVkAccount[] {
  const next = listVkAccounts().filter((a) => a.uid !== uid);
  writeVkAccounts(next);
  return next;
}

export function vkAccountsFull(): boolean {
  return listVkAccounts().length >= MAX_VK_ACCOUNTS;
}

/** uid активного VK-аккаунта (из кэша активной сессии). */
export function getActiveVkUid(): string | null {
  try {
    const raw = storageGet(ACTIVE_CACHE_KEY);
    if (!raw) return null;
    const { backendUser, user } = JSON.parse(raw) as { backendUser?: BackendUser; user?: User };
    return vkAccountUid(backendUser, user) || null;
  } catch {
    return null;
  }
}

/** Делает сохранённый аккаунт активным (пишет токен + кэш активной сессии). */
export function setActiveVkAccount(uid: string): StoredVkAccount | null {
  const acc = listVkAccounts().find((a) => a.uid === uid);
  if (!acc) return null;
  storageSet(ACTIVE_TOKEN_KEY, acc.token);
  storageSet(ACTIVE_CACHE_KEY, JSON.stringify({ user: acc.user, backendUser: acc.backendUser }));
  return acc;
}
